import React from "react";
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion } from "framer-motion";

const FAQData = [
  {
    question: "What is GX Tickets?",
    answer:
      "GX Tickets is a powerful, user-friendly digital event management platform that allows event organizers to create, sell, and manage event digital tickets with ease. Whether you’re hosting a concert, sporting, conference, church service, or fundraiser, GX Tickets gives you the tools to sell more and manage smarter.",
  },
  {
    question: "Who can use GX Tickets?",
    answer:
      "GX Tickets is ideal for event organizers of all sizes—from solo hosts to large venues and non-profits and production teams. No technical experience is needed to get started.",
  },
  {
    question: "What kind of management tools does GX Tickets offer?",
    answer: `GX Tickets includes robust event management features such as:
- Real-time sales dashboards
- Entry and section control
- Multi-user access with role permissions
- On-site check-in tools
- Event duplication for recurring events
- Access to sales and attendance reports
- Team-based selling and commission tracking`,
  },
  {
    question: "Can I add multiple team members to help manage my event?",
    answer:
      "Yes. GX Tickets allows you to add co-hosts, promoters, or staff members to your event with different permission levels—such as full access, view-only, or sales-only—so you stay in control while delegating tasks.",
  },
  {
    question: "How can I manage ticket tiers or different entry options?",
    answer:
      "GX Tickets lets you create multiple ticket types (e.g., VIP, General Admission, Early Bird) with custom pricing, time-based availability, and quantity limits.",
  },
  {
    question: "Can I track who is selling tickets and how much they’ve sold?",
    answer:
      "Absolutely. GX Tickets includes built-in team sales tracking. You can invite sellers, assign custom promo codes or links, and view their performance in real-time.",
  },
  {
    question: "Is there a way to control who gets access to certain tickets?",
    answer:
      "Yes. You can create hidden or password-protected tickets, set access based on group permissions, or release tickets to specific lists before the public.",
  },
  {
    question: "How do I start selling tickets with GX Tickets?",
    answer:
      "It’s simple. Just create an event, set up your ticket types, connect a payment method, and publish your event. You can start selling immediately online or through your customized event page.",
  },
  {
    question: "Can attendees resell or transfer tickets?",
    answer:
      "Yes. GX Tickets supports ticket transfers and optional resale features that allow attendees to upgrade or sell their tickets within your guidelines—perfect for seat swaps or last-minute changes.",
  },
  {
    question: "What payment options does GX Tickets support?",
    answer:
      "GX Tickets supports major credit/debit cards, Apple Pay, Google Pay, and custom options depending on your payment processor (e.g., Stripe or Square).",
  },
  {
    question: "Can I offer discounts or promo codes?",
    answer:
      "Yes. Create fixed-amount or percentage-off promo codes with expiration dates, usage limits, or exclusive access for VIPs or marketing campaigns.",
  },
  {
    question: "Does GX Tickets offer analytics and reporting?",
    answer: `Yes. GX Tickets includes a full suite of analytics tools, including:
- Sales trends
- Attendee demographics
- Ticket scan and check-in reports
- Sales by team member or promo code`,
  },
  {
    question: "Is there customer support available if I need help?",
    answer:
      "Yes. GX Tickets offers live chat support, a help center with tutorials, and optional onboarding services for larger organizations.",
  },
  {
    question: "Can I brand my event page or tickets?",
    answer:
      "Absolutely. GX Tickets allows full customization of your event page, ticket design, and communication templates to match your brand.",
  },
];

const PrivacyFAQ = () => {
  return (
    <Box>
      <Container maxWidth="lg">
        <Typography variant="h4" textAlign="center" sx={{ ...styles.title }}>
          Frequently Asked{" "}
          <span style={{ color: "#535353" }} className="count-shine">
            Questions (FAQs)
          </span>
          – GX Tickets
        </Typography>

        {/* FAQ List */}
        <Box sx={{ mt: 6, maxWidth: "800px", mx: "auto" }}>
          {FAQData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
            >
              <Accordion sx={styles.accordion}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#535353" }} />}
                >
                  <Typography sx={styles.question}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyFAQ;

const styles = {
  title: {
    fontWeight: 700,
    fontSize: { xs: 24, sm: 32, md: 40 },
  },
  accordion: {
    mb: 2,
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    "&:before": { display: "none" }, // Removes default MUI divider line
  },
  question: {
    fontWeight: 700,
    fontSize: "18px",
  },
};
